/**
 * Help Center article page
 * Displays a single help article with markdown content
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, Tag } from 'lucide-react';
import {
  getHelpCategories,
  getHelpArticles,
  getArticleBySlug,
  getPopularArticles,
  trackArticleView,
  PopularArticle,
} from '../../services/helpContent';
import { HelpCategory, HelpArticle, HelpArticleSummary } from '../../types';
import {
  HelpCategorySidebar,
  HelpArticleContent,
  HelpBreadcrumb,
  HelpCTA,
  HelpArticleCard,
} from '../../components/help';

export function HelpArticlePage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [allArticles, setAllArticles] = useState<HelpArticleSummary[]>([]);
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [popularArticles, setPopularArticles] = useState<PopularArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Find current category
  const currentCategory = useMemo(() => {
    if (!article) return null;
    return categories.find(c => c.id === article.categoryId);
  }, [categories, article]);

  // Related articles (same category, excluding current)
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allArticles
      .filter(a => a.categoryId === article.categoryId && a.slug !== article.slug)
      .slice(0, 3);
  }, [allArticles, article]);

  // Load data
  useEffect(() => {
    async function loadData() {
      if (!slug) return;

      try {
        setLoading(true);
        const [cats, arts, popular, art] = await Promise.all([
          getHelpCategories().catch(() => []),
          getHelpArticles().catch(() => []),
          getPopularArticles().catch(() => []),
          getArticleBySlug(slug),
        ]);

        setCategories(cats);
        setAllArticles(arts);
        setPopularArticles(popular);
        setArticle(art);
        setError(art ? null : 'Article not found');
      } catch (err) {
        console.error('Error loading article:', err);
        setError('Failed to load article. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [slug]);

  // Set page title
  useEffect(() => {
    if (article) {
      document.title = `${article.metaTitle || article.title} - Help Center - Foxie Systems`;
    } else {
      document.title = 'Help Center - Foxie Systems';
    }
  }, [article]);

  // Track article view (fire-and-forget)
  useEffect(() => {
    if (slug && article) {
      trackArticleView(slug);
    }
  }, [slug, article]);

  // Category name lookup for sidebar
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  // Format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Article Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "The article you're looking for doesn't exist."}
          </p>
          <Link to="/help" className="btn btn-primary">
            Back to Help Center
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container-custom py-8">
          <Link
            to="/help"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Help Center
          </Link>

          {/* Breadcrumb */}
          <HelpBreadcrumb
            items={[
              ...(currentCategory
                ? [{ label: currentCategory.name, href: `/help/category/${currentCategory.id}` }]
                : []),
              { label: article.title },
            ]}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <HelpCategorySidebar
            categories={categories}
            popularArticles={popularArticles}
            selectedCategory={article.categoryId}
            getCategoryName={getCategoryName}
          />

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            <article className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 md:p-8">
              {/* Article Header */}
              <header className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  {article.title}
                </h1>

                {/* Meta info */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  {currentCategory && (
                    <Link
                      to={`/help/category/${currentCategory.id}`}
                      className="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      <Tag size={14} />
                      {currentCategory.name}
                    </Link>
                  )}
                  {article.publishedAt && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(article.publishedAt)}
                    </span>
                  )}
                  {article.viewCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Eye size={14} />
                      {article.viewCount.toLocaleString()} views
                    </span>
                  )}
                  {article.featured && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
                      Popular
                    </span>
                  )}
                </div>
              </header>

              {/* Article Content */}
              <HelpArticleContent content={article.content} />

              {/* Keywords */}
              {article.keywords && article.keywords.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Related topics:</p>
                  <div className="flex flex-wrap gap-2">
                    {article.keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Related Articles
                </h2>
                <div className="space-y-3">
                  {relatedArticles.map(relatedArticle => (
                    <HelpArticleCard key={relatedArticle.id} article={relatedArticle} />
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>

        {/* CTA Section */}
        <HelpCTA />
      </div>
    </div>
  );
}

export default HelpArticlePage;
